import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BankConnectChecklistModal from "../../components/stripe/BankConnectChecklistModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import tmdbApi from "../../api/tmdbApi";
import { contentContext } from "../../context/contentContext";
import { describeStripeRequirements } from "../../utils/stripeRequirements";
import { paidBookingsPaused } from "../stripe/paidBookings";

// Dismissal is per-client, not per-browser: two people sharing a machine
// shouldn't inherit each other's hidden reminders, and a client who dismissed it
// should still find it hidden after logging back in.
const dismissStorageKey = () => `bankConnectBannerDismissed:${localStorage.getItem("userId") || "anon"}`;

const readDismissed = () => {
  try {
    return localStorage.getItem(dismissStorageKey()) === "true";
  } catch (err) {
    // Private-mode / blocked storage: the reminder simply stays visible.
    return false;
  }
};

// Shown on the Dashboard and Profile whenever the client's bank isn't fully
// connected yet - a standing reminder (per the design spec's Decision 2) rather
// than a one-time blocking screen, since Add Store already enforces the
// requirement itself.
//
// Props:
//   returnRoute        - where Stripe sends the client back to; defaults to the
//                        page they clicked from.
//   showWhenConnected  - also render a short confirmation once payouts are on.
//                        Off by default: the Dashboard shouldn't carry a standing
//                        green strip about a thing that needs no action. Profile
//                        turns it on, because "is my bank set up?" is a question
//                        clients go to an account page to answer, and silence
//                        doesn't answer it.
const BankConnectBanner = ({ returnRoute, showWhenConnected = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(contentContext);
  const [bankStatus, setBankStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // Two separate things: `dismissed` outlives the session (localStorage), while
  // `showDismissNote` lasts only until they navigate away - just long enough to
  // tell them where the reminder went.
  const [dismissed, setDismissed] = useState(readDismissed);
  const [showDismissNote, setShowDismissNote] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await tmdbApi.getStripeConnectStatus();
        // Leaving bankStatus null hides the banner entirely, which is what we want
        // when an expired session means we don't actually know the bank state.
        if (!cancelled && !tmdbApi.isSessionExpired(response)) setBankStatus(response?.result || response);
      } catch (err) {
        console.error("getStripeConnectStatus error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The modal's own "continue to Stripe" action - not a navigation, a direct
  // hand-off, same as every other entry point into onboarding.
  const handleContinueToStripe = async () => {
    setConnecting(true);
    try {
      const response = await tmdbApi.createStripeConnectOnboarding();
      const result = response?.result || response;
      const onboardingUrl = result?.onboardingUrl;

      if (!onboardingUrl) {
        toast.error("Could not start bank account setup. Please try again.", { position: "bottom-center" });
        setConnecting(false);
        return;
      }

      // The banner is no longer Dashboard-only, so send the client back to
      // wherever they clicked from rather than always to the Dashboard.
      localStorage.setItem("stripeOnboardingNextRoute", returnRoute || location.pathname || "/dashboard");
      localStorage.setItem("stripeOnboardingMandatory", "");
      window.location.href = onboardingUrl;
    } catch (err) {
      console.error("createStripeConnectOnboarding error:", err);
      toast.error("Something went wrong starting bank account setup.", { position: "bottom-center" });
      setConnecting(false);
    }
  };

  // Hiding the reminder for good is only fair if the client is told where the
  // task went - Business Profile has its own bank section and can start Stripe
  // onboarding from scratch, so that's the restart path we hand them.
  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissStorageKey(), "true");
    } catch (err) {
      console.error("Could not persist bank banner dismissal:", err);
    }
    setDismissed(true);
    setShowDismissNote(true);
  };

  // The linked bank account itself (bank name, last 4, status, "Update Bank
  // Account") lives on Business Profile, so the banner points there rather than
  // repeating those details.
  const handleOpenBusinessProfile = () => {
    navigate("/businessprofile");
  };

  // The bank account belongs to the brand, and only a Brand Manager can act on
  // it or open Business Profile (the same gate the header puts on its edit
  // shortcut) - so nobody else gets nagged about it.
  const isBrandManager = userData?.usertype === "BRAND MANAGER";

  // A status call that failed is not evidence of a missing bank account, so stay
  // quiet on error rather than nagging a client who is already set up.
  if (loading || !isBrandManager || !bankStatus) return null;

  const hasBankAccount = bankStatus?.bankConnected === true;
  // Whatever Business Profile can tell them about the account, condensed to one
  // line - enough to recognise it, not a second copy of that page.
  const accountLine = [
    bankStatus?.bankName,
    bankStatus?.bankLast4 ? `**** ${bankStatus.bankLast4}` : null
  ].filter(Boolean).join(" \u00b7 ");

  const renderDismissedNote = () => (
    <div className="col-12" id="bank-connect-dismissed-note-col">
      <div
        id="bank-connect-dismissed-note"
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <span className="material-symbols-outlined" style={{ color: '#64748b', fontSize: '20px' }} aria-hidden="true">info</span>
        <p style={{ margin: 0, flex: 1, minWidth: '240px', fontSize: '13px', color: '#475569' }} id="bank-connect-dismissed-note-text">
          This reminder won't be shown again. You can {bankStatus?.payoutsEnabled ? 'provide the information' : 'connect your bank account'} any time from your Business Profile.
        </p>
        <button
          type="button"
          onClick={handleOpenBusinessProfile}
          id="bank-connect-dismissed-note-profile-btn"
          style={{
            background: 'transparent',
            border: '1px solid #94a3b8',
            color: '#475569',
            borderRadius: '10px',
            padding: '6px 18px',
            fontSize: '11.5px',
            fontWeight: 600,
            textTransform: 'uppercase'
          }}
        >
          Go to Business Profile
        </button>
      </div>
    </div>
  );

  // Payouts on, but Stripe has deferred some requirements (SSN last 4, date of
  // birth, ...) to a volume threshold and will pause payouts if it doesn't get them
  // by then. Worth one dismissable nudge while everything still works - the
  // alternative is the red "payouts paused" banner later. Same dismissal as the
  // ordinary reminders; Business Profile keeps the prompt regardless.
  const upcomingLabels = describeStripeRequirements(bankStatus?.eventuallyDue);

  // Payouts still on while Stripe has turned transfers off: payoutsEnabled looks
  // healthy, but no paid booking can be taken, so this must fall through to the red
  // RESTRICTED banner below rather than the confirmation.
  const paused = paidBookingsPaused(bankStatus);

  // Payouts on: nothing to chase, so this is a confirmation rather than a
  // banner - and only where the caller asked for one.
  if (bankStatus?.payoutsEnabled === true && !paused) {
    if (upcomingLabels.length > 0 && !dismissed) {
      return (
        <>
          <ToastContainer position="bottom-right" id="bankConnectBannerToast" />
          <div className="col-12" id="bank-upcoming-requirements-col">
            <div
              id="bank-upcoming-requirements-banner"
              style={{
                background: '#fefce8',
                border: '1px solid #ca8a04',
                borderRadius: '10px',
                padding: '18px 22px',
                marginBottom: '18px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px'
              }}
            >
              <span className="material-symbols-outlined" style={{ color: '#ca8a04', fontSize: '26px' }} aria-hidden="true">assignment_late</span>
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: 0, color: '#854d0e', fontWeight: 700, fontSize: '15px' }} id="bank-upcoming-requirements-title">
                  Stripe needs a little more information to keep your payouts running
                </h5>
                <p style={{ margin: '4px 0 6px', color: '#854d0e', fontSize: '13.5px' }} id="bank-upcoming-requirements-text">
                  Your payouts are working today. Before they can continue past Stripe's verification
                  threshold, it will need: <strong>{upcomingLabels.join(", ")}</strong>. It takes a minute now
                  and avoids an interruption later.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                  <button
                    className="fill-btn"
                    style={{ background: '#ca8a04', borderColor: '#ca8a04' }}
                    onClick={handleContinueToStripe}
                    disabled={connecting}
                    id="bank-upcoming-requirements-provide-btn"
                  >
                    {connecting ? 'Redirecting to Stripe...' : 'Provide Information'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenBusinessProfile}
                    id="bank-upcoming-requirements-profile-btn"
                    style={{
                      background: 'transparent',
                      border: '1px solid #ca8a04',
                      color: '#854d0e',
                      borderRadius: '10px',
                      padding: '8.7px 24px',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}
                  >
                    View in Business Profile
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss this reminder"
                title="Don't show this again"
                id="bank-upcoming-requirements-dismiss-btn"
                style={{ flex: '0 0 auto', background: 'transparent', border: 'none', padding: '0', lineHeight: 1, color: '#854d0e', cursor: 'pointer' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }} aria-hidden="true">close</span>
              </button>
            </div>
          </div>
        </>
      );
    }

    // Just dismissed the nudge above: say so once, same as the other reminders,
    // instead of vanishing without a word.
    if (upcomingLabels.length > 0 && dismissed && showDismissNote) return renderDismissedNote();

    if (!showWhenConnected) return null;

    return (
      <div className="col-12" id="bank-connected-strip-col">
        <div
          id="bank-connected-strip"
          style={{
            background: '#f0fdf4',
            border: '1px solid #16a34a',
            borderRadius: '10px',
            padding: '16px 22px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '14px'
          }}
        >
          <span className="material-symbols-outlined" style={{ color: '#16a34a', fontSize: '26px' }} aria-hidden="true">check_circle</span>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <h5 style={{ margin: 0, color: '#166534', fontWeight: 700, fontSize: '15px' }} id="bank-connected-strip-title">
              Bank account connected
            </h5>
            <p style={{ margin: '3px 0 0', color: '#166534', fontSize: '13.5px' }} id="bank-connected-strip-text">
              {accountLine ? `${accountLine} - payouts` : 'Payouts'} from card and QR sales are sent to this account.
            </p>
          </div>
          <button
            type="button"
            onClick={handleOpenBusinessProfile}
            id="bank-connected-strip-manage-btn"
            style={{
              background: 'transparent',
              border: '1px solid #16a34a',
              color: '#166534',
              borderRadius: '10px',
              padding: '8.7px 24px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}
          >
            Manage in Business Profile
          </button>
        </div>
      </div>
    );
  }

  const restricted = bankStatus?.status === "RESTRICTED" || paused;
  // "Started and waiting on Stripe" is a different problem from "never started" -
  // told the wrong one, a client redoes work they already finished.
  const inProgress = hasBankAccount || bankStatus?.detailsSubmitted === true;

  const tone = restricted
    ? { bg: '#fef2f2', border: '#dc2626', icon: '#dc2626', text: '#991b1b' }
    : { bg: '#fefce8', border: '#ca8a04', icon: '#ca8a04', text: '#854d0e' };

  const copy = paused
    ? {
        icon: 'error',
        title: 'Paid bookings are paused',
        text: "Stripe has paused transfers to your account, so your paid plans are hidden from the SpotSaver app. Continue with Stripe to see what's needed - your paid plans come back on their own once it's resolved.",
        cta: 'Continue with Stripe'
      }
    : restricted
    ? {
        icon: 'error',
        title: 'Your payouts are paused',
        text: "Stripe needs something more before it can pay you. Open your Business Profile to see exactly what's outstanding, or continue with Stripe to provide it now.",
        cta: 'Continue with Stripe'
      }
    : inProgress
      ? {
          icon: 'hourglass_top',
          title: 'Finish connecting your bank account',
          text: "Stripe hasn't switched your payouts on yet. If you didn't get to the end, continue below and pick up where you left off. If you already submitted everything, Stripe is still verifying and will email you.",
          cta: 'Continue with Stripe'
        }
      : {
          icon: 'account_balance',
          title: 'Connect your bank account to get paid',
          text: 'Your earnings stay on hold until Stripe has your bank details. It takes about 5-10 minutes, and you can pause and come back.',
          cta: 'Connect Bank Account'
        };

  // Dismissal is honoured for the ordinary reminders, but not while payouts are
  // RESTRICTED: that state means Stripe has already stopped paying them, and a
  // reminder hidden weeks earlier shouldn't keep an active failure off screen.
  if (dismissed && !restricted) {
    // Only right after the click - on the next page load nothing renders at all.
    if (!showDismissNote) return null;
    return renderDismissedNote();
  }

  return (
    <>
      <ToastContainer position="bottom-right" id="bankConnectBannerToast" />
      <div className="col-12" id="dashboard-bank-connect-banner-col">
        <div
          id="dashboard-bank-connect-banner"
          style={{
            background: tone.bg,
            border: `1px solid ${tone.border}`,
            borderRadius: '10px',
            padding: '18px 22px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px'
          }}
        >
          <span className="material-symbols-outlined" style={{ color: tone.icon, fontSize: '26px' }} aria-hidden="true">{copy.icon}</span>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, color: tone.text, fontWeight: 700, fontSize: '15px' }} id="dashboard-bank-connect-title">
              {copy.title}
            </h5>
            <p style={{ margin: '4px 0 12px', color: tone.text, fontSize: '13.5px' }} id="dashboard-bank-connect-text">
              {copy.text}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
              <button
                className="fill-btn"
                style={{ background: tone.border, borderColor: tone.border }}
                onClick={() => setShowModal(true)}
                id="dashboard-continue-with-stripe-btn"
              >
                {copy.cta}
              </button>
              <button
                type="button"
                onClick={handleOpenBusinessProfile}
                id="dashboard-bank-connect-view-profile-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${tone.border}`,
                  color: tone.text,
                  borderRadius: '10px',
                  padding: '8.7px 24px',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}
              >
                {hasBankAccount ? 'View bank account' : 'Go to Business Profile'}
              </button>
            </div>
          </div>
          {/* No close button while payouts are RESTRICTED - dismissing it would be
              ignored on the next load anyway, so don't offer the click. */}
          {!restricted && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss bank account reminder"
            title="Don't show this again"
            id="dashboard-bank-connect-dismiss-btn"
            style={{
              flex: '0 0 auto',
              background: 'transparent',
              border: 'none',
              padding: '0',
              lineHeight: 1,
              color: tone.text,
              cursor: 'pointer'
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }} aria-hidden="true">close</span>
          </button>
          )}
        </div>
      </div>

      <BankConnectChecklistModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onContinue={handleContinueToStripe}
        connecting={connecting}
      />
    </>
  );
};

export default BankConnectBanner;
